import type { ReactNode } from 'react';
import { Webhook } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { isConnectableChannelKind, type ChannelKind } from '@/lib/api/channel-connections';
import type { BcConnection } from '@/lib/api/connections';

import { CHANNEL_CATALOGUE, type ChannelCatalogueEntry } from './channel-catalogue';
import { IntegrationCard, type IntegrationCardStatus } from './IntegrationCard';

/**
 * One tile in Step 3's card grid per ADR 0016. Platforms we ship
 * today (only `bigcommerce` at MVP) get a Connect CTA that opens an
 * instructions modal (BC-initiated install per ADR 0011); the rest
 * render as muted "Coming soon" tiles.
 *
 * BC connection state surfaces via the `bc_connection` field on
 * `MyTenant` (already returned by `GET /tenants/me`). When connected,
 * the tile shows "Connected as {store_hash}" with no actionable CTA
 * — BC connections are revoked by uninstalling Mailchute from the BC
 * admin (handled by the existing `/bc/uninstall` webhook), not by a
 * Mailchute-side delete endpoint.
 *
 * Future channels will plug in their own per-platform Connect dialog
 * (Shopify OAuth, Amazon SP-API LWA, custom REST endpoint, …) per
 * ADR 0016's per-platform auth-divergence section. The card chrome
 * stays identical via the shared `<IntegrationCard>` primitive.
 */
interface ChannelCardProps {
  readonly kind: ChannelKind;
  readonly bcConnection?: BcConnection;
  readonly onConnect: () => void;
}

export function ChannelCard({ kind, bcConnection, onConnect }: ChannelCardProps) {
  const entry = CHANNEL_CATALOGUE[kind];
  const connectable = isConnectableChannelKind(kind);
  const isConnected = kind === 'bigcommerce' && bcConnection?.status === 'active';
  const status: IntegrationCardStatus = isConnected
    ? 'active'
    : connectable
      ? 'connectable'
      : 'coming-soon';

  return (
    <IntegrationCard
      testId={`channel-card-${kind}`}
      logo={renderChannelLogo(entry)}
      displayName={entry.displayName}
      tagline={entry.tagline}
      status={status}
    >
      {isConnected ? (
        <div className="space-y-1 text-center">
          <p className="text-muted-foreground text-xs">
            Connected as{' '}
            <span className="text-foreground font-medium">
              {bcConnection.bc_store_hash ?? 'your store'}
            </span>
          </p>
          <p className="text-muted-foreground text-[11px]">
            Disconnect by uninstalling Mailchute from your BigCommerce admin.
          </p>
        </div>
      ) : (
        <Button
          type="button"
          size="sm"
          disabled={!connectable}
          onClick={onConnect}
          aria-label={`Connect ${entry.displayName}`}
          className="w-full"
        >
          Connect
        </Button>
      )}
    </IntegrationCard>
  );
}

/**
 * Build the logo slot. Three shapes:
 *   - `image` + `darkBackground`: wrap in a black rounded container
 *     so white-on-transparent marks (TikTok) stay readable.
 *   - `image`: plain `<img>` with the standard size constraints.
 *   - `icon`: Lucide glyph for platforms without a usable brand mark
 *     (the Custom Integration tile uses `Webhook`).
 */
function renderChannelLogo(entry: ChannelCatalogueEntry): ReactNode {
  if (entry.logo.kind === 'icon') {
    return <Webhook className="text-foreground/70 size-10" strokeWidth={1.5} aria-hidden="true" />;
  }
  if (entry.logo.darkBackground) {
    return (
      <div className="flex h-10 items-center rounded-md bg-black px-3">
        <img
          src={entry.logo.url}
          alt={`${entry.displayName} logo`}
          className="h-6 w-auto max-w-[140px] object-contain"
        />
      </div>
    );
  }
  return (
    <img
      src={entry.logo.url}
      alt={`${entry.displayName} logo`}
      className="h-10 w-auto max-w-[160px] object-contain"
    />
  );
}
